/**
 * Text-overlay animation math, shared by every surface that draws overlays:
 * the Remotion/GPU export, the in-app Player, and the editor canvas. Keeping a
 * single implementation here is what keeps those surfaces from drifting — they
 * used to carry two separate, inconsistent engines.
 *
 * Pure functions: progress + frame in, style out. No React, no Remotion hooks,
 * no DOM. Cinematic feel comes from the eased curves in `./easing`.
 */
import {
  clamp01,
  mix,
  easeOutCubic,
  easeOutQuint,
  easeOutExpo,
  easeOutBack,
  easeInCubic,
} from './easing'
import { resolveOverlayAnimation } from './textPresets'

/** Enter animations that reveal text glyph-by-glyph rather than as a block. */
export const CHARACTER_ENTER_ANIMATIONS = new Set(['typewriter', 'wave', 'letter_by_letter'])

export interface AnimResult {
  opacity: number
  transform?: string
  filter?: string
  textSlice?: number
  perCharStyle?: (i: number, total: number) => React.CSSProperties
}

export function computeEnterEffect(type: string, p: number, frame: number): AnimResult {
  // Two eased clocks: `o` for opacity (gentle), `m` for distance/scale travel
  // (hard deceleration — the cinematic "fast arrival, long settle").
  const o = easeOutCubic(p)
  const m = easeOutExpo(p)
  switch (type) {
    // A clean fade carries a whisper of rise + settle so it reads as intentional.
    case 'fade': return { opacity: o, transform: `translateY(${mix(10, 0, m)}px) scale(${mix(0.985, 1, m)})` }
    case 'slide_up': return { opacity: o, transform: `translateY(${mix(48, 0, m)}px)` }
    case 'slide_down': return { opacity: o, transform: `translateY(${mix(-48, 0, m)}px)` }
    case 'slide_left': return { opacity: o, transform: `translateX(${mix(64, 0, m)}px)` }
    case 'slide_right': return { opacity: o, transform: `translateX(${mix(-64, 0, m)}px)` }
    // Tasteful scale-up with a faint de-blur — a weighted settle into frame.
    case 'scale': return { opacity: o, transform: `scale(${mix(0.9, 1, m)})`, filter: `blur(${mix(4, 0, m)}px)` }
    case 'typewriter': return { opacity: 1, textSlice: Math.ceil(easeOutQuint(p) * 100) }
    case 'glitch': {
      const seed = Math.sin(frame * 47) * 43758.5453
      const r = seed - Math.floor(seed)
      return {
        opacity: Math.min(1, p * 1.5),
        transform: `translate(${(r - 0.5) * 8}px, ${(Math.sin(frame * 31) * 0.5) * 6}px)`,
        filter: `hue-rotate(${Math.sin(frame * 13) * 30}deg)`,
      }
    }
    // Restrained overshoot pop — punchy without the amateur rubber-band.
    case 'bounce': {
      const b = easeOutBack(p)
      return { opacity: Math.min(1, p * 2.4), transform: `scale(${mix(0.62, 1, b)})` }
    }
    // Drifts in out of focus with a touch of scale — the signature film entrance.
    case 'blur_in': return { opacity: o, filter: `blur(${mix(18, 0, m)}px)`, transform: `scale(${mix(1.05, 1, m)})` }
    case 'zoom_in': return { opacity: o, transform: `scale(${mix(1.55, 1, m)})` }
    case 'rotate_in': return { opacity: o, transform: `rotate(${mix(-14, 0, m)}deg) scale(${mix(0.82, 1, m)})` }
    case 'wave': {
      return {
        opacity: o,
        perCharStyle: (i: number) => {
          const offset = Math.sin((frame * 0.15) + i * 0.5) * 8
          return { display: 'inline-block', transform: `translateY(${offset}px)` }
        },
      }
    }
    // Each glyph rides its own staggered, eased fade + rise + de-blur. Overlapping
    // windows keep it flowing rather than popping one letter at a time.
    case 'letter_by_letter': {
      const spread = 0.62 // fraction of the window spent staggering starts
      return {
        opacity: 1,
        perCharStyle: (i: number, total: number) => {
          const start = total > 1 ? (i / total) * spread : 0
          const local = easeOutCubic(clamp01((p - start) / (1 - spread)))
          return {
            display: 'inline-block',
            opacity: local,
            transform: `translateY(${mix(20, 0, local)}px) scale(${mix(0.86, 1, local)})`,
            filter: local < 1 ? `blur(${mix(8, 0, local)}px)` : undefined,
          }
        },
      }
    }
    default: return { opacity: o }
  }
}

export function computeExitEffect(type: string, p: number, frame: number): AnimResult {
  // Opacity lingers then drops (1 - easeInCubic); motion accelerates away (easeInCubic).
  const op = 1 - easeInCubic(p)
  const m = easeInCubic(p)
  switch (type) {
    case 'fade': return { opacity: op, transform: `translateY(${mix(0, -10, m)}px) scale(${mix(1, 1.012, m)})` }
    case 'slide_up': return { opacity: op, transform: `translateY(${-44 * m}px)` }
    case 'slide_down': return { opacity: op, transform: `translateY(${44 * m}px)` }
    case 'slide_left': return { opacity: op, transform: `translateX(${-60 * m}px)` }
    case 'slide_right': return { opacity: op, transform: `translateX(${60 * m}px)` }
    case 'scale': return { opacity: op, transform: `scale(${mix(1, 1.08, m)})` }
    case 'typewriter': return { opacity: op, textSlice: 100 }
    case 'glitch': {
      const seed = Math.sin(frame * 47) * 43758.5453
      const r = seed - Math.floor(seed)
      return {
        opacity: op,
        transform: `translate(${(r - 0.5) * 8}px, ${(Math.sin(frame * 31) * 0.5) * 6}px)`,
        filter: `hue-rotate(${Math.sin(frame * 13) * 30}deg)`,
      }
    }
    case 'bounce': return { opacity: op, transform: `scale(${mix(1, 0.7, m)})` }
    case 'blur_in':
    case 'blur_out': return { opacity: op, filter: `blur(${20 * m}px)`, transform: `scale(${mix(1, 1.04, m)})` }
    case 'zoom_in':
    case 'zoom_out': return { opacity: op, transform: `scale(${mix(1, 1.3, m)})` }
    case 'rotate_in':
    case 'rotate_out': return { opacity: op, transform: `rotate(${15 * m}deg) scale(${mix(1, 0.82, m)})` }
    case 'wave': return { opacity: op }
    case 'letter_by_letter': return { opacity: op }
    default: return { opacity: op }
  }
}

export function computeEmphasisEffect(type: string, frame: number, fps: number): AnimResult {
  const t = frame / fps
  switch (type) {
    case 'pulse': return { opacity: 1, transform: `scale(${1 + 0.03 * Math.sin(t * 3)})` }
    case 'breathe': return { opacity: 0.85 + 0.15 * Math.sin(t * 2) }
    case 'float': return { opacity: 1, transform: `translateY(${4 * Math.sin(t * 1.5)}px)` }
    case 'glow_pulse': return { opacity: 1, filter: `brightness(${1 + 0.15 * Math.sin(t * 2.5)})` }
    case 'color_shift': return { opacity: 1, filter: `hue-rotate(${(t * 30) % 360}deg)` }
    default: return { opacity: 1 }
  }
}

/**
 * Resolve the full enter → hold → exit animation for an overlay at a given
 * progress (0..1 across its lifetime). Pulls enter/exit/emphasis from the
 * overlay's preset when the overlay itself doesn't specify them.
 */
export function computeOverlayAnimation(
  overlay: {
    preset?: string
    animation?: string
    enterAnimation?: string
    exitAnimation?: string
    emphasisAnimation?: string
    animationDuration?: number
    enterDuration?: number
    exitDuration?: number
    duration: number
  },
  progress: number,
  frame: number,
  fps: number,
): AnimResult {
  // Merge the preset's animation fields — the agent usually sets only `preset`,
  // so without this its titles would render dead-static.
  const eff = resolveOverlayAnimation(overlay)
  const enterType = eff.enterAnimation || eff.animation || 'none'
  const exitType = eff.exitAnimation || eff.animation || 'none'
  const emphasisType = eff.emphasisAnimation

  if (enterType === 'none' && exitType === 'none' && !emphasisType) return { opacity: 1 }

  const baseDur = eff.animationDuration || 0.5
  const enterDur = eff.enterDuration || baseDur
  const exitDur = eff.exitDuration || baseDur
  const total = overlay.duration

  const enterEnd = Math.min(enterDur / total, 0.4)
  const exitStart = Math.max(1 - exitDur / total, 0.6)

  if (progress < enterEnd && enterType !== 'none') {
    return computeEnterEffect(enterType, progress / enterEnd, frame)
  }
  if (progress > exitStart && exitType !== 'none') {
    return computeExitEffect(exitType, (progress - exitStart) / (1 - exitStart), frame)
  }

  // Hold phase: emphasis animation, or continue wave's per-char effect.
  if (emphasisType && emphasisType !== 'none') {
    return computeEmphasisEffect(emphasisType, frame, fps)
  }
  if (enterType === 'wave') {
    return {
      opacity: 1,
      perCharStyle: (i: number) => ({
        display: 'inline-block' as const,
        transform: `translateY(${Math.sin((frame * 0.15) + i * 0.5) * 8}px)`,
      }),
    }
  }
  return { opacity: 1 }
}
